import { Component, HostListener, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSidenavModule } from '@angular/material/sidenav';
import { AccountList } from '../account/account-list/account-list';
import { StockService } from './stock.service';
import {
  Stock as StockModel,
  GetStocksRequest,
  StockCounts,
  StockStatus,
  STOCK_STATUSES,
} from './stock.type';
import { StockFormDialog } from './stock-form-dialog/stock-form-dialog';
import { StockReadinessDialog } from './stock-readiness-dialog/stock-readiness-dialog';
import { StockRunsDialog } from './stock-runs-dialog/stock-runs-dialog';
import { ConfirmDialog } from '../shared/confirm-dialog/confirm-dialog';
import { Header } from '../shared/header/header';
import { SupplierFilter } from '../shared/components/supplier-filter/supplier-filter';
import { SupplierService } from '../supplier/supplier.service';
import { SupplierProfile } from '../supplier/supplier.type';
import { SupplierInfoDialog } from '../supplier/supplier-info-dialog/supplier-info-dialog';
import { STOCK_PROBLEM_LABELS, isStockProblemWarning } from './stock-problems';
import { AuthService } from '../auth/auth.service';

/**
 * Persistent account stocks.
 *
 * The daily-stock workflow is gone: there is no date filter, no "copy
 * yesterday's stocks", and no per-day fee update. An account has exactly one
 * stock row that is armed, drained, refilled and re-armed forever, so the
 * filters here are status, supplier and search — never a calendar date.
 * Historical reporting lives in the runs dialog and the daily report screen,
 * which is where date filters belong.
 */
@Component({
  selector: 'app-stock',
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    SupplierFilter,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    MatTooltipModule,
    MatPaginatorModule,
    MatSidenavModule,
    AccountList,
    Header,
  ],
  templateUrl: './stock.html',
  styleUrl: './stock.scss'
})
export class Stock implements OnInit {
  readonly canEdit = inject(AuthService).canWrite('contracts');
  readonly statuses = STOCK_STATUSES;
  isMobile = window.innerWidth <= 768;

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth <= 768;
  }

  displayedColumns = [
    'select', 'email', 'supplier', 'status', 'remained', 'headroom', 'threshold', 'updated', 'actions',
  ];

  stocks: StockModel[] = [];
  counts: StockCounts | null = null;
  suppliers: SupplierProfile[] = [];

  loading = false;
  error: string | null = null;

  pageIndex = 0;
  pageSize = 25;
  total = 0;

  filterStatus: StockStatus | '' = '';
  filterSupplierId = '';
  search = '';

  selected = new Set<string>();

  constructor(
    private stockService: StockService,
    private supplierService: SupplierService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
  ) { }

  ngOnInit(): void {
    this.loadSuppliers();
    this.load();
  }

  loadSuppliers(): void {
    this.supplierService.listSuppliers({ pageSize: 200 }).subscribe({
      next: res => this.suppliers = res.suppliers,
      error: () => { /* the supplier filter is optional; the list still works */ },
    });
  }

  load(): void {
    this.loading = true;
    this.error = null;
    const request: GetStocksRequest = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
    };
    if (this.filterStatus) request.status = this.filterStatus;
    if (this.filterSupplierId) request.supplierId = this.filterSupplierId;
    if (this.search.trim()) request.search = this.search.trim();

    this.stockService.getStocks(request).subscribe({
      next: res => {
        this.stocks = res.stocks;
        this.total = res.total;
        this.counts = res.counts ?? null;
        this.loading = false;
      },
      error: err => {
        this.error = err?.error?.error ?? 'Failed to load accounts';
        this.loading = false;
      },
    });
  }

  applyFilters(): void {
    this.pageIndex = 0;
    this.selected.clear();
    this.load();
  }

  onSupplierChange(supplierId: string): void {
    this.filterSupplierId = supplierId;
    this.applyFilters();
  }

  clearFilters(): void {
    this.filterStatus = '';
    this.filterSupplierId = '';
    this.search = '';
    this.applyFilters();
  }

  onPage(e: PageEvent): void {
    this.pageIndex = e.pageIndex;
    this.pageSize = e.pageSize;
    this.selected.clear();
    this.load();
  }

  // ── selection ──

  toggle(id: string): void {
    if (this.selected.has(id)) this.selected.delete(id);
    else this.selected.add(id);
  }

  get allSelected(): boolean {
    return this.stocks.length > 0 && this.stocks.every(s => this.selected.has(s.id));
  }

  toggleAll(): void {
    if (this.allSelected) this.stocks.forEach(s => this.selected.delete(s.id));
    else this.stocks.forEach(s => this.selected.add(s.id));
  }

  // ── lifecycle ──

  arm(stock?: StockModel): void {
    const targets = stock ? [stock] : this.stocks.filter(s => this.selected.has(s.id));
    if (targets.length === 0) return;

    this.dialog.open(StockReadinessDialog, {
      width: '420px',
      data: { stocks: targets },
    }).afterClosed().subscribe(threshold => {
      if (threshold === undefined || threshold === null) return;
      const ids = targets.map(s => s.id);
      const done = (updated: number, skipped: number) => {
        this.snackBar.open(
          skipped > 0
            ? `${updated} armed, ${skipped} skipped (not in a changeable state)`
            : `${updated} account(s) armed`,
          'Close', { duration: 4000 });
        this.selected.clear();
        this.load();
      };

      if (ids.length === 1) {
        this.stockService.markReady(ids[0], { coins_to_be_remain: threshold }).subscribe({
          next: () => done(1, 0),
          error: err => this.snackBar.open(err?.error?.error ?? 'Failed', 'Close', { duration: 5000 }),
        });
      } else {
        this.stockService.bulkMarkReady({ ids, coins_to_be_remain: threshold }).subscribe({
          next: res => done(res.updated, res.skipped?.length ?? 0),
          error: err => this.snackBar.open(err?.error?.error ?? 'Failed', 'Close', { duration: 5000 }),
        });
      }
    });
  }

  disarm(stock?: StockModel): void {
    const ids = stock ? [stock.id] : [...this.selected];
    if (ids.length === 0) return;

    const done = (updated: number) => {
      this.snackBar.open(`${updated} account(s) marked not ready`, 'Close', { duration: 4000 });
      this.selected.clear();
      this.load();
    };

    if (ids.length === 1) {
      this.stockService.markNotReady(ids[0]).subscribe({
        next: () => done(1),
        error: err => this.snackBar.open(err?.error?.error ?? 'Failed', 'Close', { duration: 5000 }),
      });
    } else {
      this.stockService.bulkMarkNotReady(ids).subscribe({
        next: res => done(res.updated),
        error: err => this.snackBar.open(err?.error?.error ?? 'Failed', 'Close', { duration: 5000 }),
      });
    }
  }

  // ── admin-only ──

  openSupplierInfoDialog(stock: StockModel): void {
    const isMobile = window.innerWidth <= 768;
    this.dialog.open(SupplierInfoDialog, {
      data: { supplierId: stock.supplier_id, supplierUsername: stock.supplier_name || stock.supplier },
      width: isMobile ? '100vw' : undefined,
      minWidth: isMobile ? undefined : '420px',
      maxWidth: isMobile ? '100vw' : '95vw',
      maxHeight: '90vh',
    });
  }

  viewRuns(stock: StockModel): void {
    this.dialog.open(StockRunsDialog, { width: '900px', maxWidth: '900px', data: { stock } });
  }

  create(): void {
    this.dialog.open(StockFormDialog, {
      width: '520px',
      data: { suppliers: this.suppliers },
    }).afterClosed().subscribe(created => {
      if (created) this.load();
    });
  }

  edit(stock: StockModel): void {
    this.dialog.open(StockFormDialog, {
      width: '520px',
      data: { stock, suppliers: this.suppliers },
    }).afterClosed().subscribe(saved => {
      if (saved) this.load();
    });
  }

  remove(): void {
    const ids = [...this.selected];
    if (ids.length === 0) return;
    this.dialog.open(ConfirmDialog, {
      data: {
        title: 'Delete accounts',
        message: `Delete ${ids.length} account(s)? Accounts that are currently transferring are skipped.`,
        confirmText: 'Delete',
      },
    }).afterClosed().subscribe(ok => {
      if (!ok) return;
      this.stockService.deleteStocks(ids).subscribe({
        next: res => {
          this.snackBar.open(`${res.deleted} account(s) deleted`, 'Close', { duration: 4000 });
          this.selected.clear();
          this.load();
        },
        error: err => this.snackBar.open(err?.error?.error ?? 'Failed', 'Close', { duration: 5000 }),
      });
    });
  }

  statusClass(status: StockStatus): string {
    return `status-${status.toLowerCase()}`;
  }

  /** Coins a run could actually spend: balance above whichever floor applies. */
  headroom(s: StockModel): number {
    return s.remained_coins - (s.effective_floor ?? s.coins_to_be_remain);
  }

  problemLabel(s: StockModel): string {
    return s.problem_code ? (STOCK_PROBLEM_LABELS[s.problem_code] ?? '') : '';
  }

  /** Top-up/re-arm problems read as warnings; a broken account reads as an error. */
  problemClass(s: StockModel): string {
    return isStockProblemWarning(s.problem_code) ? 'warn' : 'fail';
  }

  trackById(_: number, s: StockModel): string {
    return s.id;
  }
}
